/**
 * Zustand Cloud Sync Helper
 *
 * Provides utilities for syncing Zustand store state to PostgreSQL.
 * Uses userSettingsService for backend communication.
 *
 * Pattern:
 * - localStorage: Immediate writes (Zustand persist)
 * - PostgreSQL: Async sync for cross-device persistence
 *
 * CRITICAL: Cloud sync only runs when user is authenticated.
 * This prevents pre-auth API calls from triggering 401 errors and infinite token refresh loops.
 */

import { getSetting, setSetting } from '@shared/services/userSettingsService';
import { logger } from '@shared/utils/logger';

/**
 * Check if user is authenticated.
 *
 * CRITICAL: This prevents cloud sync from running before login,
 * which would trigger 401 errors and infinite token refresh loops.
 */
const isAuthenticated = (): boolean => {
  if (typeof window === 'undefined') return false;

  const authData = localStorage.getItem('dms-auth-storage');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      return parsed.state?.isAuthenticated === true;
    } catch {
      return false;
    }
  }
  return false;
};

export interface CloudSyncConfig {
  /** PostgreSQL setting key (e.g., 'kanban-view-settings') */
  key: string;
  /** Namespace for logical grouping */
  namespace?: string;
  /** Debounce delay in ms for sync operations */
  debounceMs?: number;
}

// Track pending sync operations to prevent duplicates
const pendingSync = new Map<string, NodeJS.Timeout>();

/**
 * Create cloud sync methods for a Zustand store.
 *
 * @param config - Sync configuration
 * @returns Object with syncToCloud and loadFromCloud methods
 *
 * @example
 * const { syncToCloud, loadFromCloud } = createCloudSync({
 *   key: 'kanban-view-settings',
 *   namespace: 'ui-preferences',
 *   debounceMs: 1000,
 * });
 */
export function createCloudSync<T>(config: CloudSyncConfig) {
  const { key, namespace = 'zustand', debounceMs = 500 } = config;
  const syncKey = `${namespace}/${key}`;

  /**
   * Sync current state to PostgreSQL (debounced).
   *
   * CRITICAL: Only syncs when user is authenticated.
   * This prevents pre-auth API calls from triggering 401 errors.
   *
   * @param state - Current store state to sync
   */
  async function syncToCloud(state: T): Promise<void> {
    // CRITICAL FIX: Skip cloud sync if not authenticated
    // This prevents pre-auth API calls from triggering token refresh infinite loops
    if (!isAuthenticated()) {
      // Silent skip - don't log on every state change when not authenticated
      return;
    }

    // Clear any pending sync
    const pending = pendingSync.get(syncKey);
    if (pending) {
      clearTimeout(pending);
    }

    // Debounce sync operation
    const timeout = setTimeout(async () => {
      // Double-check auth status after debounce (user might have logged out)
      if (!isAuthenticated()) {
        pendingSync.delete(syncKey);
        return;
      }

      try {
        await setSetting(key, state, { namespace, syncToLocal: false });
        logger.log(`[CloudSync] Synced ${key} to PostgreSQL`);
      } catch (error) {
        logger.error(`[CloudSync] Failed to sync ${key}:`, error);
      } finally {
        pendingSync.delete(syncKey);
      }
    }, debounceMs);

    pendingSync.set(syncKey, timeout);
  }

  /**
   * Load state from PostgreSQL.
   *
   * @returns State from cloud or null if not found
   */
  async function loadFromCloud(): Promise<T | null> {
    try {
      const cloudState = await getSetting<T>(key, {
        namespace,
        fallbackToLocal: false,
      });

      if (cloudState) {
        logger.log(`[CloudSync] Loaded ${key} from PostgreSQL`);
      }

      return cloudState;
    } catch (error) {
      logger.error(`[CloudSync] Failed to load ${key}:`, error);
      return null;
    }
  }

  return { syncToCloud, loadFromCloud };
}

/**
 * Create auto-sync middleware for Zustand persist.
 *
 * Automatically syncs to cloud on every state change.
 *
 * @example
 * const useStore = create<StoreState>()(
 *   persist(
 *     subscribeWithAutoSync(
 *       (set, get) => ({ ... }),
 *       { key: 'my-store', namespace: 'ui' }
 *     ),
 *     { name: 'my-store' }
 *   )
 * );
 */
export function createAutoSyncSubscriber<T>(
  config: CloudSyncConfig,
  partialize?: (state: T) => Partial<T>
) {
  const { syncToCloud } = createCloudSync<Partial<T>>(config);

  return (set: (state: T) => void, get: () => T) => {
    // Return a wrapper that syncs after each state change
    return (partial: Partial<T> | ((state: T) => Partial<T>)) => {
      // Call original set
      if (typeof partial === 'function') {
        set({ ...get(), ...partial(get()) } as T);
      } else {
        set({ ...get(), ...partial } as T);
      }

      // Sync to cloud
      const state = get();
      const toSync = partialize ? partialize(state) : state;
      syncToCloud(toSync as Partial<T>);
    };
  };
}
